class ModelInfoPopup extends HTMLElement {
  constructor() {
    super();
    this.modelData = [];
    this._initialized = false;
    this._loadingPromise = null;
    this._popupInitialized = false;
    this._connected = false;
  }

  connectedCallback() {
    if (this._connected) return;
    this._connected = true;
    this.setupLazyInit();
  }

  setupLazyInit() {
    if (this._initialized) return;
    this.init();
  }

  async init() {
    if (this._initialized) return;
    this._initialized = true;
    await this.loadModelData();
    this.initPopupInteractions();
  }

  async loadModelData() {
    if (this._loadingPromise) {
      return this._loadingPromise;
    }
    this._loadingPromise = this._loadModelData();
    return this._loadingPromise;
  }

  async _loadModelData() {
    const modelsJsonAttr =
      this.getAttribute('data-models-json') || this.getAttribute('data-models');

    if (modelsJsonAttr) {
      try {
        const parsed = JSON.parse(modelsJsonAttr);
        this.modelData = Array.isArray(parsed) ? parsed : [];
        if (this.modelData.length) {
          this.render();
          return;
        }
      } catch (error) {
        console.error('Model Info Popup - Unable to parse metafield data', error);
      }
    }

    // Collect the exact handles present in this gallery before fetching
    const galleryScope =
      this.closest('.product-gallery') ||
      this.closest('[data-product-single-media-group]') ||
      this.closest('[data-section-id]') ||
      document;
    const requiredHandles = ModelInfoPopup.getGalleryHandles(galleryScope);

    try {
      const metaobjectRecords = await this.fetchModelDataFromAPI(requiredHandles);
      const formatted = this.buildModelData(metaobjectRecords);
      this.modelData = formatted.length ? formatted : {};

      // this.modelData = this.getSampleData(); // REMOVE THIS LINE TO DISABLE SAMPLE DATA
    } catch (error) {
      console.error('Model Info Popup - Failed to load model data', error);
    }

    this.render();
  }

  buildModelData(records) {
    if (!Array.isArray(records) || records.length === 0) {
      return [];
    }

    // Records are already scoped to the needed handles by fetchModelDataFromAPI; just transform each one directly.
    return records.map(entry => this.transformEntry(entry));
  }

  transformEntry(entry) {
    const parts = [];

    if (entry.model_size) {
      parts.push(`is wearing size <strong>${entry.model_size}</strong>.`);
    }

    const measurements = [];
    if (entry.model_height) measurements.push(`Height <strong>${entry.model_height}</strong>`);
    if (entry.model_bust) measurements.push(`Bust <strong>${entry.model_bust}</strong>`);
    if (entry.model_waist) measurements.push(`Waist <strong>${entry.model_waist}</strong>`);
    if (entry.model_hips) measurements.push(`Hips <strong>${entry.model_hips}</strong>`);

    if (measurements.length) {
      parts.push(measurements.join(', '));
    }

    const derivedHandle = entry.handle || ModelInfoPopup.normalizeName(entry.first_name);
    const displayName = entry.first_name || entry.handle || 'Model';
    const description = entry.model_description || parts.join(' ');

    return {
      model_name: displayName,
      model_description: description,
      model_image: entry.model_image,
      handle: derivedHandle
    };
  }

  async fetchModelDataFromAPI(requiredHandles) {
    if (!requiredHandles || requiredHandles.length === 0) return [];

    // Only fetch handles not already in the cache
    const toFetch = requiredHandles.filter(h => !(h in ModelInfoPopup.handleCache));

    if (toFetch.length) {
      if (!ModelInfoPopup.metaobjectDataPromise) {
        ModelInfoPopup.metaobjectDataPromise = ModelInfoPopup.loadMetaobjectDataFromGraphQL(toFetch);
      }

      try {
        const freshRecords = await ModelInfoPopup.metaobjectDataPromise;
        toFetch.forEach(h => {
          ModelInfoPopup.handleCache[h] = freshRecords.find(r => r.handle === h) || null;
        });
      } catch (error) {
        ModelInfoPopup.metaobjectDataPromise = null;
        throw error;
      }
    }

    return requiredHandles.map(h => ModelInfoPopup.handleCache[h]).filter(Boolean);
  }

  static async loadMetaobjectDataFromGraphQL(handles) {
    if (!handles || handles.length === 0) return [];

    // Request only the specific handles needed — one alias per handle in a single query.
    // This replaces the old paginated fetch-all approach.
    const fields = `
      handle
      first_name: field(key: "first_name") { value }
      last_name: field(key: "last_name") { value }
      model_bust: field(key: "model_bust") { value }
      model_hips: field(key: "model_hips") { value }
      model_waist: field(key: "model_waist") { value }
      model_height: field(key: "model_height") { value }
      model_size: field(key: "model_size") { value }
      model_image: field(key: "model_image") { value }
    `;
    const aliases = handles
      .map((h, i) => `m_${i}: metaobject(handle: {handle: "${h}", type: "model"}) { ${fields} }`)
      .join('\n');
    const query = `query GetModelByHandle { ${aliases} }`;

    const response = await ModelInfoPopup.executeGraphQL(query);
    if (!response || !response.data) return [];

    const collected = [];
    handles.forEach((h, i) => {
      const node = response.data[`m_${i}`];
      if (!node) return;
      collected.push({
        handle:       node.handle || h,
        first_name:   node.first_name?.value || '',
        last_name:    node.last_name?.value || '',
        model_bust:   node.model_bust?.value || null,
        model_hips:   node.model_hips?.value || null,
        model_waist:  node.model_waist?.value || null,
        model_height: node.model_height?.value || null,
        model_size:   node.model_size?.value || null,
        model_image:  node.model_image?.value || null
      });
    });

    // Resolve media image GIDs to image URLs in bulk
    const mediaGids = Array.from(new Set(collected.map(i => i.model_image).filter(Boolean)));
    if (mediaGids.length) {
      const images = await ModelInfoPopup.fetchMediaImageUrls(mediaGids);
      collected.forEach(item => {
        if (item.model_image && images[item.model_image]) {
          item.model_image = images[item.model_image];
        } else {
          item.model_image = null;
        }
      });
    }

    return collected;
  }

  // Execute a GraphQL query against the Storefront API
  static async executeGraphQL(query) {
    try {
      const shop = (window.Shopify && window.Shopify.shop) ? window.Shopify.shop : window.location.host;
      const token = window.theme?.apiKeys?.storefrontAccessToken || '';
      const url = `https://${shop}/api/2026-01/graphql`;

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/graphql',
          ...(token ? { 'X-Shopify-Storefront-Access-Token': token } : {})
        },
        body: query
      });

      if (!res.ok) {
        throw new Error(`GraphQL request failed: ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Model Info Popup - GraphQL execute failed', error);
      throw error;
    }
  }

  // Fetch node image URLs for MediaImage GIDs using nodes(ids: [...])
  static async fetchMediaImageUrls(gids) {
    if (!gids || gids.length === 0) return {};

    // GraphQL nodes query
    const idsString = gids.map(g => `"${g}"`).join(',');
    const query = `query GetMediaImages { nodes(ids: [${idsString}]) { __typename ... on MediaImage { id image { url } } } }`;

    try {
      const res = await ModelInfoPopup.executeGraphQL(query);
      const nodes = res?.data?.nodes || [];
      const result = {};
      nodes.forEach(n => {
        if (n && n.__typename === 'MediaImage' && n.id && n.image && n.image.url) {
          result[n.id] = n.image.url;
        }
      });
      return result;
    } catch (error) {
      console.error('Model Info Popup - fetchMediaImageUrls failed', error);
      return {};
    }
  }

  static normalizeName(value) {
    if (!value) {
      return '';
    }
    return value.toString().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  static getGalleryHandles(scope = document) {
    if (typeof document === 'undefined') {
      return [];
    }

    const root = scope || document;
    const handles = new Set();
    root.querySelectorAll('[data-model-handle]').forEach(node => {
      const attrValue = node.getAttribute('data-model-handle') || node.dataset.modelHandle;
      // Strip the optional "model_" / "model-" prefix that themes sometimes add
      const clean = attrValue ? attrValue.toString().trim().replace(/^model[_-]?/i, '') : '';
      if (clean) handles.add(clean);
    });

    return Array.from(handles);
  }

  getSampleData() {
    return [
      {
        model_name: 'Alice',
        model_description: '5\'7" / Size S',
        handle: 'alice'
      },
      {
        model_name: 'Bob',
        model_description: '6\'0" / Size M',
        handle: 'bob'
      },
      {
        model_name: 'Carol',
        model_description: '5\'9" / Size M',
        handle: 'carol'
      }
    ];
  }

  render() {
    const showModel = this.getAttribute('data-show-model') === 'true';
    const isQuickView = this.getAttribute('data-quick-view') === 'true';

    const isOnlyGeneric = Array.isArray(this.modelData) && this.modelData.length > 0 &&
      this.modelData.every(m => (m.model_name || m.first_name || '').toLowerCase().trim() === 'generic');

    console.log('[ModelInfoPopup] modelData:', JSON.stringify(this.modelData), 'isOnlyGeneric:', isOnlyGeneric);

    if (!showModel || isQuickView || this.modelData.length === 0 || isOnlyGeneric) {
      this.style.display = 'none';
      this.innerHTML = '';
      return;
    }

    const chooseModelText = this.getAttribute('data-choose-text') || 'Choose a model';
    const displayAllText = this.getAttribute('data-display-all-text') || 'Display all';
    const defaultImage = 'data:image/webp;base64,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';

    this.style.display = '';
    this.className = 'model-info-popup';
    this.setAttribute('data-toggle', 'model-info');

    this.innerHTML = `
      <div class="model-info-popup__wrapper">
        <div class="model-info-popup__arrow">
          <svg width="13" height="7" viewBox="0 0 13 7" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.82945 6.82912L12.6626 0.995777C12.8905 0.768004 12.8905 0.398636 12.6626 0.170825C12.4348 -0.056948 12.0655 -0.0569481 11.8377 0.170864L6.41692 5.59171L0.995913 0.170824C0.768101 -0.0569485 0.398773 -0.0569486 0.170961 0.170863C0.0570555 0.28473 0.00012207 0.434025 0.00012207 0.58332C0.00012207 0.732614 0.0570564 0.881909 0.171 0.995815L6.0045 6.82912C6.11389 6.93856 6.26226 7 6.41696 7C6.57166 7 6.72006 6.93856 6.82945 6.82912Z" fill="#475360"></path>
          </svg>
        </div>
        <div class="model-info-inner">
          <span>${chooseModelText}</span>
        </div>
        ${this.modelData.map(model => {
          const handle = model.handle || this.handleize(model.model_name);
          const modelImage = model.model_image || defaultImage;
          return `
          <div class="model-info-inner" data-value="model_${handle}">
            <img src="${modelImage}" alt="${model.model_name}" width="24" height="24" loading="lazy" fetchpriority="low" />
            <span>
              ${model.model_name}${model.model_description ? ' ' + model.model_description : ''}
            </span>
          </div>
        `;
        }).join('')}
        <div class="model-info-inner" data-value="all">
          <span>
            <strong>${displayAllText}</strong>
          </span>
        </div>
      </div>
    `;
  }

  initPopupInteractions() {
    if (this._popupInitialized) return;

    const popup = this;
    const content = popup.querySelector('.model-info-popup__wrapper');
    const items = popup.querySelectorAll('.model-info-inner');

    if (!content || items.length < 2) return;

    this._popupInitialized = true;

    const trigger = items[0];
    const listItems = Array.from(items).slice(1);
    let currentSelected = null;
    const chooseModelText = this.getAttribute('data-choose-text') || 'Choose a model'; 

    popup.addEventListener('click', event => {
      const clickedItem = event.target.closest('.model-info-inner');
      if (clickedItem && !clickedItem.isSameNode(trigger)) return;

      if (popup.classList.contains('open')) {
        popup.classList.remove('open');
        const containerEl = popup.closest('model-info-popup') || popup.closest('.model-info-popup') || popup;
        if (containerEl && containerEl.style) {
          containerEl.style.paddingBottom = '';
        }
        return;
      }

      popup.classList.add('open');
      content.style.height = 'auto';
      const containerEl = popup.closest('model-info-popup') || popup.closest('.model-info-popup') || popup;
      if (containerEl && containerEl.style) {
        const needed = content.scrollHeight + 12; // small offset
        //containerEl.style.paddingBottom = needed + 'px';
      }
    });

    listItems.forEach(item => {
      item.addEventListener('click', event => {
        event.stopPropagation();

        if (currentSelected) {
          currentSelected.style.display = '';
        }

        const selectedValue = item.dataset.value;

        if (selectedValue === 'all') {
          trigger.innerHTML = `<span>${chooseModelText}</span>`;
          trigger.dataset.value = '';
          listItems.forEach(i => i.style.display = '');
          currentSelected = null;
        } else {
          trigger.innerHTML = item.innerHTML;
          trigger.dataset.value = item.dataset.value;
          item.style.display = 'none';
          currentSelected = item;
        }

        popup.classList.remove('open');

        const gallery = popup.closest('.product-gallery');
        if (!gallery) return;

        filterGalleryByModel(gallery, selectedValue);

        movePopupToFirstVisibleSlide(popup, gallery);

        const containerEl = popup.closest('model-info-popup') || popup.closest('.model-info-popup') || popup;
        if (containerEl && containerEl.style) {
          containerEl.style.paddingBottom = '';
        }
      });
    });
  }

  handleize(str) {
    return str
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  setModelData(data) {
    this.modelData = data;
    this.render();
  }
}

// Per-handle cache shared across all instances on the page
ModelInfoPopup.handleCache = {};
ModelInfoPopup.metaobjectDataPromise = null;

if (!customElements.get('model-info-popup')) {
  customElements.define('model-info-popup', ModelInfoPopup);
}

function getFlickity(container) {
  if (!container) return null;
  if (window.theme && window.theme.Flickity) {
    return window.theme.Flickity.data(container);
  }
  if (window.Flickity) {
    return window.Flickity.data(container);
  }
  return null;
}

function refreshFlickity(container) {
  if (!container) return;

  const flkty = getFlickity(container);
  if (!flkty) return;

  const options = { ...flkty.options };
  flkty.destroy();

  const slider = container.querySelector('.flickity-slider');
  if (slider) {
    slider.style.transform = 'translateX(0)';
    slider.style.left = '0';
  }

  container
    .querySelectorAll('.is-selected, .is-nav-selected')
    .forEach(el => {
      el.classList.remove('is-selected', 'is-nav-selected');
    });

  let cellSelector = '';
  let isThumbs = false;

  if (
    container.matches('[data-product-single-media-slider]') ||
    container.classList.contains('product-gallery__media-slider')
  ) {
    cellSelector = '.product-gallery__media-slide:not(.image-hidden)';
  } else if (
    container.matches('[data-product-single-media-thumbs]') ||
    container.classList.contains('product-single__thumbnails')
  ) {
    cellSelector = '.product-single__thumbnail:not(.image-hidden)';
    isThumbs = true;
  }

  const newFlkty = new window.theme.Flickity(container, {
    ...options,
    cellSelector,
    ...(isThumbs
      ? {
          cellAlign: 'left',
          contain: true,
          wrapAround: false,
          initialIndex: 0
        }
      : {})
  });

  newFlkty.reloadCells();
  newFlkty.select(0, false, true);
  newFlkty.positionCells();
  newFlkty.reposition();
  newFlkty.resize();
}

function movePopupToFirstVisibleSlide(popup, gallery) {
  if (!popup || !gallery) return;

  const slides = gallery.querySelectorAll('.product-gallery__media-slide');

  const firstVisibleSlide = Array.from(slides).find(slide => {
    return slide.offsetParent !== null && !slide.classList.contains('image-hidden');
  });

  if (firstVisibleSlide) {
    if (!firstVisibleSlide.contains(popup)) {
      firstVisibleSlide.appendChild(popup);
    }
  } else if (!gallery.contains(popup)) {
    gallery.appendChild(popup);
  }
}

function normalizeModelKey(value) {
  if (!value) return '';
  return value
    .toString()
    .toLowerCase()
    .replace(/^model[_-]?/i, '')
    .replace(/[^a-z0-9]/g, '');
}

function getNodeModelKey(node) {
  if (!node) return '';
  const raw = node.getAttribute('data-model-handle') || node.dataset.modelHandle || '';
  return normalizeModelKey(raw);
}

function matchesSelectedModel(selectedValue, node) {
  if (!selectedValue || selectedValue === 'all') return true;
  const selectedKey = normalizeModelKey(selectedValue);
  if (!selectedKey) return true;
  const nodeKey = getNodeModelKey(node);
  if (!nodeKey) return false;
  return nodeKey === selectedKey;
}

function applyModelFilter(nodes, selectedValue) {
  if (!nodes || nodes.length === 0) return;

  nodes.forEach(node => {
    const match = matchesSelectedModel(selectedValue, node);
    node.classList.toggle('image-hidden', !match);
  });

  if (![...nodes].some(node => !node.classList.contains('image-hidden'))) {
    nodes.forEach(node => node.classList.remove('image-hidden'));
  }
}

function filterGalleryByModel(gallery, selectedValue) {
  const slides = gallery.querySelectorAll('.product-gallery__media-slide');
  const thumbs = gallery.querySelectorAll('.product-single__thumbnail');

  applyModelFilter(slides, selectedValue);
  applyModelFilter(thumbs, selectedValue);

  const mediaSlider =
    gallery.querySelector('.product-gallery__media-slider') ||
    gallery.querySelector('[data-product-single-media-slider]');

  const thumbsSlider =
    gallery.querySelector('.product-single__thumbnails') ||
    gallery.querySelector('[data-product-single-media-thumbs]');

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      refreshFlickity(mediaSlider);
      refreshFlickity(thumbsSlider);
    });
  });
}
